import { v4 as uuid } from 'uuid';
import { FindIframeWindow } from './FindIframe';

export const InitialComponentPath = 'InitialComponentPath';

/** Format of Post Messages.  Based on Flux Standard Actions. */
export interface PostMessage<T extends string> extends MinimalPostMessageContents {
  /** Associates messages with a handler */
  type: T;
}

/** A reply message (when used in `PostMessageSender.sendReply`).
 * Same as a regular message, but doesn't contain the `type` field. */
export interface PostMessageReply extends MinimalPostMessageContents {}

interface MinimalPostMessageContents {
  /** The contents of the message */
  payload?: unknown;

  /** If this message represents an error occurring */
  error?: true;
}

/** The internal shape of the Post Message contract, with metadata appended to track iframe instance and replies */
export interface PostMessageWithMetadata<T extends string> extends PostMessage<T> {
  /** Additional internal fields used by the postMessage library */
  meta?: PostMessageMetadata;
}

export interface PostMessageMetadata {
  /** A unique message used to associate one message instance with another */
  messageId?: string;

  /** An identifier used to disambiguate between multiple frames sending messages to a single frame */
  senderId?: string;

  /** An identifier used to route to the correct subComponent's set of handlers on guest side */
  componentId?: string;
}

/** Set of functions used to send post messages */
export interface PostMessageSender {
  /** Sends the given message */
  send<T extends string>(message: PostMessage<T>): void;

  /**
   * Sends the given message but allows for a value to be returned
   * @param message the message to send
   * @param timeout if we should timeout this message if we don't get a reply
   * within a time window
   * @returns a promise for the return value
   */
  sendWithReturn<T extends string>(message: PostMessage<T>, timeout?: boolean): Promise<PostMessageReply>;

  /**
   * Sends a message as a reply to another message.
   * Note: must be used in conjunction with a message that had been sent via `sendWithReturn`,
   *   or else the reply will simply get lost.
   * @param originalMessage the original message which was received
   * @param replyMessage the new message being sent as a reply
   */
  sendReply<T extends string>(originalMessage: PostMessage<T>, replyMessage: PostMessageReply): void;

  /**
   * Ensures that there are no more event listeners registered
   */
  dispose(): void;
}

const messageTimeoutMs = 3000;

/**
 * Creates a variant of sender which passes componentId in metadata
 * @param parentSender the parent's sender
 * @param componentId the child's componentId which should be stamped on the postMessage
 */
export function createChildPostMessageSender(parentSender: PostMessageSender, componentId: string): PostMessageSender {
  return {
    send: (message: PostMessageWithMetadata<any>): void => {
      parentSender.send(addMetadataToMessage(message, { componentId }));
    },
    sendWithReturn: (message: PostMessageWithMetadata<any>, timeout?: boolean) => {
      return parentSender.sendWithReturn(addMetadataToMessage(message, { componentId }), timeout);
    },
    sendReply: (message: PostMessageWithMetadata<any>, replyMessage: PostMessage<any>): void => {
      parentSender.sendReply(addMetadataToMessage(message, { componentId }), replyMessage);
    },
    dispose: (): void => {
      // Do nothing, parent sender should handle it's own dispose
    }
  };
}

/**
 * Creates functions used to send messages to the given target
 * @param sourceWindow the window that is sending the messages
 * @param targetWindow the window to send the messages to
 * @param targetOrigin the origin associated with the window we're sending messages to
 * @param senderId Must be specified if message being sent from child frame
 */
export function createPostMessageSender(
  sourceWindow: Window,
  findTargetWindow: FindIframeWindow,
  targetOrigin: string,
  senderId?: string
): PostMessageSender {
  validateOrigin(targetOrigin);

  const sendInternal = (message: PostMessage<string>): void => {
    const targetWindow = findTargetWindow();
    targetWindow && targetWindow.postMessage(JSON.stringify(message), targetOrigin);
  };

  const listenerTracker = createMessageListenerTracker(sourceWindow);

  return {
    send(message: PostMessageWithMetadata<string>): void {
      sendInternal(addMetadataToMessage(message, { senderId }));
    },
    sendWithReturn<T extends string, S extends string>(
      message: PostMessageWithMetadata<T>,
      timeout?: boolean
    ): Promise<PostMessage<S>> {
      const messageId = uuid();

      let timeoutId: number | undefined;

      return new Promise<PostMessage<S>>((resolve, reject) => {
        const listener = (event: MessageEvent) => {
          const message = messageEventToPostMessage<S>(event, targetOrigin);

          if (message && message.meta && message.meta.messageId === messageId) {
            listenerTracker.remove(listener);
            resolve(message);
          }
        };

        listenerTracker.add(listener);

        const messageWithMeta: PostMessageWithMetadata<T> = addMetadataToMessage(message, { messageId, senderId });
        sendInternal(messageWithMeta);

        if (timeout) {
          timeoutId = sourceWindow.setTimeout(() => {
            reject('sendWithReply timeout');
            timeoutId = undefined;
          }, messageTimeoutMs);
        }
      }).then((msg) => {
        if (timeoutId !== undefined) {
          sourceWindow.clearTimeout(timeoutId);
        }
        return msg;
      });
    },
    sendReply<T extends string, S extends string>(
      originalMessage: PostMessageWithMetadata<T>,
      replyMessage: PostMessage<S>
    ): void {
      if (originalMessage.meta) {
        const messageWithMeta: PostMessageWithMetadata<S> = addMetadataToMessage(replyMessage, {
          messageId: originalMessage.meta.messageId,
          senderId
        });
        sendInternal(messageWithMeta);
      }
    },
    dispose(): void {
      listenerTracker.removeAll();
    }
  };
}

function addMetadataToMessage(message: PostMessageWithMetadata<any>, additionalMeta: PostMessageMetadata) {
  return { ...message, meta: { ...message.meta, ...additionalMeta } };
}

type MessageListener = (event: MessageEvent) => void;

interface MessageListenerTracker {
  add(listener: MessageListener): void;
  remove(listener: MessageListener): void;
  removeAll(): void;
}

export function createMessageListenerTracker(window: Window): MessageListenerTracker {
  let listeners: readonly MessageListener[] = [];

  return {
    add(listener: MessageListener) {
      window.addEventListener('message', listener);
      listeners = listeners.concat(listener);
    },
    remove(listener: MessageListener) {
      listeners = listeners.filter((item) => {
        if (item === listener) {
          window.removeEventListener('message', listener);
          return false;
        }

        return true;
      });
    },
    removeAll() {
      listeners.forEach((listener) => window.removeEventListener('message', listener));
      listeners = [];
    }
  };
}

export type PostMessageHandlers<T extends string> = {
  [postMessageType: string]: ((message: PostMessage<T>) => void) | undefined;
};

export type PostMessageHandlersCollection<T extends string> = {
  [componentId: string]: PostMessageHandlers<T> | undefined;
};

/**
 * Registers a set of post message handlers under the current window
 * @param sourceWindow the window that is sending the messages
 * @param senderOrigin what origin we expect to receive messages from
 * @param handlers the handlers for the different message types
 * @param senderId Must be set when receiving from child frame
 * @returns a callback for removing the listener from the current window
 */
export function setupPostMessageReceiver<T extends string>(
  sourceWindow: Window,
  senderOrigin: string,
  handlers: PostMessageHandlers<T>,
  senderId?: string
): { remove: () => void } {
  validateOrigin(senderOrigin);

  const listener = (event: MessageEvent) => {
    const message = messageEventToPostMessage<T>(event, senderOrigin);

    if (message) {
      if (senderId && (message.meta && message.meta.senderId) !== senderId) {
        return;
      }

      const handler = handlers[message.type];
      handler && handler(message);
    }
  };

  sourceWindow.addEventListener('message', listener);

  return { remove: () => sourceWindow.removeEventListener('message', listener) };
}

/**
 * Registers a collection of post message handlers mapped by componentId under the current window
 * This handles the situation of routing sub-component postMessages
 * @param sourceWindow the window that is sending the messages
 * @param senderOrigin what origin we expect to receive messages from
 * @param handlersCollection the collection of post message handlers by componentId
 * @param rootComponentId the componentId of the root component. This will be used when no componentId is sent.
 * @param senderId Must be set when receiving from child frame
 * @returns a callback for removing the listener from the current window
 */
export function setupPostMessageCollectionReceiver<T extends string>(
  sourceWindow: Window,
  senderOrigin: string,
  handlersCollection: PostMessageHandlersCollection<T>,
  senderId?: string
): { remove: () => void } {
  validateOrigin(senderOrigin);

  const listener = (event: MessageEvent) => {
    const message = messageEventToPostMessage<T>(event, senderOrigin);

    if (message) {
      if (senderId && (message.meta && message.meta.senderId) !== senderId) {
        return;
      }

      let handlers: PostMessageHandlers<T> | undefined;

      // Route the correct set of handlers based on the provided componentId
      const componentId = message.meta?.componentId;
      if (componentId) {
        handlers = handlersCollection[componentId];
      } else {
        handlers = handlersCollection[InitialComponentPath];
      }

      if (!handlers) {
        // By the time a sub-component receives a message, its handlersCollection should already have been set up.
        // If for some reason it isn't, there is something wrong in the way that the child component was constructed.
        throw new Error(`HandlersCollection could not find handlers for ${componentId}`);
      }

      const handler = handlers[message.type];
      handler && handler(message);
    }
  };

  sourceWindow.addEventListener('message', listener);

  return { remove: () => sourceWindow.removeEventListener('message', listener) };
}

function messageEventToPostMessage<T extends string>(
  event: MessageEvent,
  expectedOrigin: string
): PostMessageWithMetadata<T> | undefined {
  if (event.origin !== expectedOrigin) {
    return undefined;
  }

  try {
    const deserializedMessage = JSON.parse(event.data);

    // If the message could not be deserialized or it does not have the right shape, ignore it.
    if (deserializedMessage && typeof deserializedMessage === 'object') {
      return deserializedMessage;
    }
  } catch {}

  return undefined;
}

function validateOrigin(origin: string) {
  // We do not allow the usage of '*' as an origin
  if (origin === '*') {
    throw new Error(`Post message origin invalid value: '${origin}'`);
  }
}

/**
 * Creates a set of post messages using the given sender (in case any of the
 * handlers need to send messages).
 */
export type PostMessageHandlerFactory = (sender: PostMessageSender) => PostMessageHandlers<string>;

// cspell:ignore deserialized
